import { normalizeToolkitKey } from '@littlebird/ui-utils';
import {
  siAirtable,
  siAtlassian,
  siBitly,
  siClickup,
  siCloudflare,
  siCloudinary,
  siGitlab,
  siHubspot,
  siIntercom,
  siLucid,
  siMake,
  siMiro,
  siMixpanel,
  siNotion,
  siPaypal,
  siPlanetscale,
  siPosthog,
  siRazorpay,
  siSanity,
  siSentry,
  siSquare,
  siStripe,
  siSupabase,
  siTickettailor,
  siTicktick,
  siTrello,
  siWebflow,
  siWhatsapp,
  siWix,
  siWordpress,
  siXero,
  type SimpleIcon,
} from 'simple-icons';

import { SlackIcon } from '@/assets/icons/slack';
import { cn } from '@/lib/utils';

// Listed by hand: a Simple Icons slug that matches a catalog id can belong to
// a different company. Egnyte and Zapier are left out because their Simple
// Icons are long wordmarks, unreadable at tile size.
const brandTileSources: Record<string, SimpleIcon> = {
  airtable: siAirtable,
  atlassian: siAtlassian,
  atlassianrovo: siAtlassian,
  bitly: siBitly,
  clickup: siClickup,
  cloudflare: siCloudflare,
  cloudflaredeveloperplatform: siCloudflare,
  cloudinary: siCloudinary,
  gitlab: siGitlab,
  hubspot: siHubspot,
  intercom: siIntercom,
  lucid: siLucid,
  make: siMake,
  miro: siMiro,
  mixpanel: siMixpanel,
  notion: siNotion,
  paypal: siPaypal,
  planetscale: siPlanetscale,
  posthog: siPosthog,
  razorpay: siRazorpay,
  sanity: siSanity,
  sentry: siSentry,
  square: siSquare,
  stripe: siStripe,
  supabase: siSupabase,
  tickettailor: siTickettailor,
  ticktick: siTicktick,
  trello: siTrello,
  webflow: siWebflow,
  whatsapp_business: siWhatsapp,
  wix: siWix,
  wordpresscom: siWordpress,
  xero: siXero,
};

const channel = (hex: string, offset: number) => {
  const value = parseInt(hex.slice(offset, offset + 2), 16) / 255;
  return value <= 0.04045 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;
};

const glyphColor = (hex: string) => {
  const luminance =
    0.2126 * channel(hex, 0) +
    0.7152 * channel(hex, 2) +
    0.0722 * channel(hex, 4);
  return luminance > 0.55 ? '#000000' : '#ffffff';
};

const brandTileCache = new Map<
  SimpleIcon,
  React.ComponentType<{ className?: string }>
>();

const SlackBrandTile = ({ className }: { className?: string }) => (
  <span
    className={cn('flex items-center justify-center bg-[#4a154b]', className)}
  >
    <SlackIcon className="size-2/3" />
  </span>
);

const brandTile = (
  icon: SimpleIcon
): React.ComponentType<{ className?: string }> => {
  const cached = brandTileCache.get(icon);
  if (cached) return cached;
  const BrandTile = ({ className }: { className?: string }) => (
    <svg
      viewBox="0 0 24 24"
      aria-hidden="true"
      className={cn('shrink-0', className)}
    >
      <rect width="24" height="24" fill={`#${icon.hex}`} />
      <path
        d={icon.path}
        fill={glyphColor(icon.hex)}
        transform="translate(5.4 5.4) scale(0.55)"
      />
    </svg>
  );
  BrandTile.displayName = `BrandTile(${icon.title})`;
  brandTileCache.set(icon, BrandTile);
  return BrandTile;
};

export const getBrandTileIcon = (
  name: string
): React.ComponentType<{ className?: string }> | undefined => {
  const key = normalizeToolkitKey(name);
  if (key === 'slack') return SlackBrandTile;
  const icon = brandTileSources[key];
  return icon && brandTile(icon);
};
